import { useState } from 'react';
import {
  CheckCircle2, AlertTriangle, ClipboardCheck, Loader2, Shield,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { ordenesAPI } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Cierre técnico (post-NSYS).
 *
 * Histórico: este modal incluía un checklist QC pesado (batería WISE.ASC,
 * funciones del sistema, limpieza, etc.) que duplicaba lo que NSYS verifica
 * automáticamente. El 2026-05-21 se simplificó: el técnico solo declara
 * el resultado de la avería (reparada/parcial/irreparable), confirma que
 * realizó el diagnóstico de salida y, si aplica, el resultado de la
 * garantía. Toda la verificación técnica fina vive ahora en `nsys_data`.
 */
export function TecnicoCierreReparacion({ orden, onRefresh }) {
  const [showDialog, setShowDialog] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Resultado de la avería: 'reparada' | 'parcial' | 'no_reparada'
  const [resultadoAveria, setResultadoAveria] = useState('reparada');
  const [motivoNoReparada, setMotivoNoReparada] = useState('');
  const [diagnosticoSalida, setDiagnosticoSalida] = useState(false);
  const [notas, setNotas] = useState('');

  // Garantía
  const esGarantia = orden?.es_garantia === true;
  const [resultadoGarantia, setResultadoGarantia] = useState('procede');
  const [motivoNoGarantia, setMotivoNoGarantia] = useState('');

  // Pre-requisitos
  const materiales       = orden.materiales || [];
  const matPendientes    = materiales.filter(m => !m.validado_tecnico);
  const hayMatPendientes = matPendientes.length > 0;
  const cpiCompletado    = Boolean(orden.cpi_opcion);
  const riCompletada     = Boolean(orden.ri_completada);
  const tieneNsys        = Boolean(orden?.nsys_data?.evaluation?.summary || orden?.nsys_parts_status?.result);

  const averiaNoReparada = resultadoAveria === 'no_reparada';
  const garantiaNoProcede = esGarantia && resultadoGarantia === 'no_procede';

  const puedeAbrir = !orden.bloqueada
    && Boolean(orden.diagnostico_tecnico)
    && !hayMatPendientes;

  const puedeConfirmar = (() => {
    if (garantiaNoProcede) return Boolean(motivoNoGarantia?.trim());
    if (averiaNoReparada)  return Boolean(motivoNoReparada?.trim());
    return diagnosticoSalida;
  })();

  const handleCerrar = async () => {
    if (!puedeConfirmar) {
      toast.error('Marca el checkbox de confirmación antes de cerrar');
      return;
    }

    const qcPayload = {
      notas_cierre_tecnico: notas?.trim() || null,
      fecha_fin_reparacion: new Date().toISOString(),
    };

    if (garantiaNoProcede) {
      qcPayload.garantia_resultado = 'no_procede';
      qcPayload.garantia_motivo_no_procede = motivoNoGarantia.trim();
      qcPayload.garantia_tests_omitidos = true;
    } else if (averiaNoReparada) {
      qcPayload.diagnostico_salida_realizado = true;
      qcPayload.qc_resultado_averia = 'no_reparada';
      qcPayload.qc_motivo_no_reparada = motivoNoReparada.trim();
    } else {
      // Reparada / parcial: marcamos el diagnóstico de salida realizado
      // y dejamos que la verificación técnica detallada la cubra NSYS.
      qcPayload.diagnostico_salida_realizado = true;
      qcPayload.qc_resultado_averia = resultadoAveria;
      if (esGarantia) qcPayload.garantia_resultado = 'procede';
    }

    const estadoDestino = averiaNoReparada ? 'irreparable' : 'reparado';
    setGuardando(true);
    try {
      const mensajeCambio = (() => {
        if (garantiaNoProcede) return `GARANTÍA NO PROCEDE: ${motivoNoGarantia.trim()}`;
        if (averiaNoReparada)  return `AVERÍA NO REPARADA: ${motivoNoReparada.trim()}`;
        if (resultadoAveria === 'parcial') return `Reparación PARCIAL: ${notas?.trim() || 'Algunas funciones siguen sin operar al 100%'}`;
        return notas?.trim() || 'Reparación completada — pasa a validación NSYS';
      })();

      await ordenesAPI.cambiarEstado(orden.id, {
        nuevo_estado: estadoDestino,
        usuario: 'tecnico',
        mensaje: mensajeCambio,
      });
      await ordenesAPI.actualizar(orden.id, qcPayload);

      const okMsg = (() => {
        if (garantiaNoProcede) return 'Orden cerrada — Garantía NO PROCEDE';
        if (averiaNoReparada)  return 'Orden marcada como IRREPARABLE — admin gestionará la devolución';
        return 'Reparación cerrada — la orden pasa a REPARADO / VALIDACIÓN';
      })();
      toast.success(okMsg);
      setShowDialog(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cerrar la reparación');
    } finally {
      setGuardando(false);
    }
  };

  if (orden.bloqueada || orden.estado !== 'en_taller') return null;

  return (
    <>
      {/* Tarjeta resumen + botón abrir cierre */}
      <Card className="border-green-200" data-testid="tecnico-cierre-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-700">
            <CheckCircle2 className="w-5 h-5" />
            Finalizar Reparación
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Pre-requisitos */}
          <div className="space-y-1.5">
            <PreReqRow ok={Boolean(orden.diagnostico_tecnico)} label="Diagnóstico técnico guardado" required />
            <PreReqRow ok={riCompletada} label="Inspección de entrada (RI) completada" required />
            <PreReqRow ok={cpiCompletado} label="Privacidad / CPI registrada" required />
            <PreReqRow ok={!hayMatPendientes} label={hayMatPendientes ? `${matPendientes.length} material(es) pendiente(s) de validar` : 'Materiales validados'} required />
            <PreReqRow ok={tieneNsys} label={tieneNsys ? 'Diagnóstico NSYS disponible' : 'Diagnóstico NSYS pendiente (opcional)'} />
          </div>

          <Button
            className="w-full h-12 bg-green-600 hover:bg-green-700 gap-2"
            onClick={() => setShowDialog(true)}
            disabled={!puedeAbrir}
            data-testid="cerrar-reparacion-btn"
          >
            <ClipboardCheck className="w-5 h-5" />
            {!puedeAbrir ? 'Completa los requisitos previos' : 'Confirmar reparación finalizada'}
          </Button>
        </CardContent>
      </Card>

      {/* Diálogo de confirmación simplificado */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-qc-final">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <ClipboardCheck className="w-5 h-5" />
              Confirmar cierre de reparación
            </DialogTitle>
            <DialogDescription>
              {orden?.dispositivo?.modelo} · {orden?.dispositivo?.imei || 'Sin IMEI'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">

            {/* Banner NSYS / sin NSYS */}
            {tieneNsys ? (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 flex items-start gap-2" data-testid="banner-nsys-ok">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800">
                    La verificación técnica (batería, funciones, ESN, FMIP…) la cubre el diagnóstico NSYS de salida.
                  </p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    No es necesario rellenar checklist manual. Solo confirma el resultado y cierra.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2" data-testid="banner-nsys-pendiente">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">
                    Esta OT aún no tiene diagnóstico NSYS de salida.
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Puedes cerrar igualmente, pero recuerda lanzar el diagnóstico NSYS antes de enviar.
                  </p>
                </div>
              </div>
            )}

            {/* Sección Garantía */}
            {esGarantia && (
              <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg space-y-3" data-testid="seccion-garantia">
                <div className="flex items-center gap-2 text-amber-700 font-semibold">
                  <Shield className="w-5 h-5" />
                  Esta es una orden de GARANTÍA
                </div>
                {orden?.indicaciones_garantia_cliente && (
                  <div className="p-2 bg-card rounded border border-amber-200">
                    <p className="text-xs text-amber-600 font-medium">Avería reportada por el cliente:</p>
                    <p className="text-sm whitespace-pre-wrap">{orden.indicaciones_garantia_cliente}</p>
                  </div>
                )}
                {orden?.indicaciones_admin_garantia && (
                  <div className="p-2 bg-blue-50 rounded border border-blue-300" data-testid="tecnico-indicaciones-admin-garantia">
                    <p className="text-xs text-blue-700 font-semibold">Observaciones del admin (recepción):</p>
                    <p className="text-sm whitespace-pre-wrap">{orden.indicaciones_admin_garantia}</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Resultado de la garantía:</Label>
                  <RadioGroup value={resultadoGarantia} onValueChange={setResultadoGarantia} className="space-y-2">
                    <div className="flex items-center space-x-2 p-3 bg-card rounded border hover:border-green-400 transition-colors">
                      <RadioGroupItem value="procede" id="garantia-procede" />
                      <Label htmlFor="garantia-procede" className="cursor-pointer flex-1">
                        <span className="font-medium text-green-700">Garantía PROCEDE</span>
                        <p className="text-xs text-muted-foreground">La avería está cubierta y será reparada bajo garantía</p>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 bg-card rounded border hover:border-red-400 transition-colors">
                      <RadioGroupItem value="no_procede" id="garantia-no-procede" />
                      <Label htmlFor="garantia-no-procede" className="cursor-pointer flex-1">
                        <span className="font-medium text-red-700">Garantía NO PROCEDE</span>
                        <p className="text-xs text-muted-foreground">La avería NO está cubierta (daño externo, mal uso, etc.)</p>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                {garantiaNoProcede && (
                  <div className="space-y-2 pt-2 border-t border-amber-200">
                    <Label className="flex items-center gap-1 text-red-700 font-medium">
                      <AlertTriangle className="w-4 h-4" />
                      Motivo de no garantía *
                    </Label>
                    <Textarea
                      value={motivoNoGarantia}
                      onChange={(e) => setMotivoNoGarantia(e.target.value)}
                      placeholder="Ej: daño por líquido detectado, golpe en chasis, manipulación por terceros..."
                      rows={3}
                      className="bg-card"
                      data-testid="textarea-motivo-no-garantia"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Resultado de avería */}
            {!garantiaNoProcede && (
              <div className="space-y-2" data-testid="seccion-resultado-averia">
                <h3 className="text-sm font-semibold border-b pb-1">Resultado de la reparación <span className="text-red-500">*</span></h3>
                <RadioGroup value={resultadoAveria} onValueChange={setResultadoAveria} className="space-y-1.5">
                  <ResultadoOption value="reparada" current={resultadoAveria} title="Avería reparada correctamente" subtitle="El dispositivo funciona como debería" color="green" testId="radio-averia-reparada" />
                  <ResultadoOption value="parcial" current={resultadoAveria} title="Reparada parcialmente" subtitle="Funciona pero con limitaciones (anota cuáles en notas)" color="amber" testId="radio-averia-parcial" />
                  <ResultadoOption value="no_reparada" current={resultadoAveria} title="La avería NO ha sido reparada" subtitle="Dispositivo irreparable. La orden pasará a IRREPARABLE." color="red" testId="radio-averia-no-reparada" />
                </RadioGroup>

                {averiaNoReparada && (
                  <div className="space-y-1.5 pt-2 border-t border-red-200">
                    <Label className="flex items-center gap-1 text-red-700 font-medium text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      Motivo por el que no se ha podido reparar *
                    </Label>
                    <Textarea
                      value={motivoNoReparada}
                      onChange={(e) => setMotivoNoReparada(e.target.value)}
                      placeholder="Ej: placa con corrosión avanzada, sin repuesto disponible, daño irreversible en chip de gestión..."
                      rows={3}
                      className="bg-card"
                      data-testid="textarea-motivo-no-reparada"
                    />
                    <p className="text-xs text-amber-700">
                      El motivo aparecerá en el PDF y se notificará al cliente.
                    </p>
                  </div>
                )}

                {/* Confirmador único */}
                {!averiaNoReparada && (
                  <label className="flex items-start gap-2 p-3 mt-3 rounded-lg border border-green-200 bg-green-50 cursor-pointer" data-testid="check-diagnostico-salida-wrap">
                    <Checkbox
                      checked={diagnosticoSalida}
                      onCheckedChange={(c) => setDiagnosticoSalida(Boolean(c))}
                      data-testid="check-diagnostico-salida"
                      className="mt-0.5"
                    />
                    <div className="text-sm">
                      <span className="font-medium">He realizado el diagnóstico de salida y verificado la reparación</span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Declaración del técnico — la trazabilidad técnica detallada queda en el informe NSYS.
                      </p>
                    </div>
                  </label>
                )}
              </div>
            )}

            {/* Notas opcionales */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold border-b pb-1">Notas adicionales</h3>
              <Textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Observaciones, recomendaciones para el cliente, próximas revisiones..."
                rows={2}
                data-testid="notas-cierre-textarea"
              />
            </div>
          </div>

          {!puedeConfirmar && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              {garantiaNoProcede
                ? 'Indica el motivo por el que la garantía no procede.'
                : averiaNoReparada
                  ? 'Indica el motivo por el que no se ha podido reparar la avería.'
                  : 'Marca la confirmación "He realizado el diagnóstico de salida y verificado la reparación".'}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCerrar}
              disabled={guardando || !puedeConfirmar}
              className={`gap-2 ${averiaNoReparada ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
              data-testid="btn-confirmar-cierre"
            >
              {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {averiaNoReparada ? 'Cerrar como IRREPARABLE' : 'Cerrar Reparación — Marcar como REPARADO'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ───────────────────────────── Helpers ───────────────────────────────────────

function PreReqRow({ ok, label, required }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok
        ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
        : <AlertTriangle className={`w-4 h-4 shrink-0 ${required ? 'text-red-500' : 'text-muted-foreground'}`} />}
      <span className={ok ? 'text-foreground' : required ? 'text-red-700' : 'text-muted-foreground'}>
        {label}
      </span>
      {required && !ok && <Badge variant="destructive" className="text-[10px] h-4">Obligatorio</Badge>}
    </div>
  );
}

function ResultadoOption({ value, current, title, subtitle, color, testId }) {
  const colorMap = {
    green: 'border-green-500 bg-green-50',
    amber: 'border-amber-500 bg-amber-50',
    red:   'border-red-500 bg-red-50',
  };
  const textMap = {
    green: 'text-green-700',
    amber: 'text-amber-700',
    red:   'text-red-700',
  };
  const active = current === value;
  return (
    <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
      active ? colorMap[color] : 'border-border hover:border-gray-300'
    }`}>
      <RadioGroupItem value={value} id={`averia-${value}`} className="mt-0.5" data-testid={testId} />
      <div className="text-sm">
        <span className={`font-medium ${textMap[color]}`}>{title}</span>
        <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
      </div>
    </label>
  );
}
